import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { campaignChecklist, campaignStatus, canPublish, type CampaignPromotion } from '@iws/theme-schema/campaign'

import { OfferModel } from '@/http/models/Offer'
import { OfferModel as OfferModelType } from '@/types'
import { configService } from '@/services'
import { useBusiness } from '@/contexts/BusinessContext'
import { revalidateStore, storefrontOrigin } from '@/lib/storefront'

import { BLOCK_CATALOG } from '@/lib/campaign-blocks/catalog'

import { WizardStep1, WizardStep2, WizardStep3, validateStep } from './validateStep'
import { usePreviewCampaign } from './usePreviewCampaign'
import { useOfferCatalog } from './useOfferCatalog'

export type { WizardStep1, WizardStep2, WizardStep3 }

export type BuilderStep = 'offer' | 'page' | 'launch'
export const BUILDER_STEPS: BuilderStep[] = ['offer', 'page', 'launch']

const getDefaultRules = (type: OfferModelType.OfferType): Partial<OfferModelType.OfferRules> => {
  switch (type) {
    case 'single_product':
      return { product_id: '', discount_type: 'percentage', discount_value: 0, free_delivery: false }
    case 'buy_x_get_y':
      return { buy_items: [], get_items: [], max_applications_per_order: 1, free_delivery: false }
    case 'volume_discount':
      return { product_id: '', tiers: [{ min_quantity: 1, discount_type: 'percentage', discount_value: 0 }] }
    case 'combo_fixed_price':
      return { items: [], price: 0, free_delivery: false }
  }
}

interface LocalDraft {
  step1: WizardStep1
  step2: WizardStep2
  step3: WizardStep3
  savedAt: number
}

const draftKey = (id: string | undefined) =>
  `campaign-draft:${configService.getConfig()?.apiConfig?.clientHost ?? 'store'}:${id ?? 'new'}`

const readDraft = (key: string): LocalDraft | null => {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as LocalDraft) : null
  } catch {
    return null
  }
}

const writeDraft = (key: string, draft: LocalDraft | null) => {
  try {
    if (draft) localStorage.setItem(key, JSON.stringify(draft))
    else localStorage.removeItem(key)
  } catch {
    // Storage full or blocked: autosave is best-effort, the server save still works.
  }
}

export const useCampaignBuilder = () => {
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const isEditMode = Boolean(id)
  const { previewLoading, previewOffer } = usePreviewCampaign()
  const { profile } = useBusiness()
  const domain = profile?.custom_customer_domain || profile?.platform_customer_domain
  const origin = storefrontOrigin(domain)

  const [step, setStep] = useState<BuilderStep>('offer')
  const [formErrors, setFormErrors] = useState<string[]>([])

  // The persisted offer's id/slug once a save has happened (or straight away in edit mode): the live
  // preview and the sharing kit need a real offer to point the storefront at.
  const [savedOffer, setSavedOffer] = useState<{ id: string; slug: string; updatedAt?: string } | null>(
    id ? { id, slug: '' } : null,
  )

  const [step1, setStep1] = useState<WizardStep1>({
    name: '',
    status: 'inactive',
    type: null,
    starts_at: '',
    ends_at: '',
    min_order_value: 0,
    max_total_uses: null,
    max_uses_per_customer: null,
    coupon_allowed: false,
  })
  const [step2, setStep2] = useState<WizardStep2>({ offer_rules: {} })
  const [step3, setStep3] = useState<WizardStep3>({
    // New campaigns are promoted on the home page and product cards unless the merchant turns it off.
    ui_content: { template: 'grid', accent_color: '#0D9E6E', promote: { homeBanner: true, productBadge: true, announcementBar: false } },
  })
  const [mobileReviewed, setMobileReviewed] = useState(false)

  // ── Load existing offer in edit mode ──────────────────────────────
  const [loadingOffer, setLoadingOffer] = useState(isEditMode)
  const [restorable, setRestorable] = useState<LocalDraft | null>(null)
  const loaded = useRef(!isEditMode)

  useEffect(() => {
    if (!isEditMode || !id) {
      setRestorable(readDraft(draftKey(undefined)))
      return
    }
    ;(async () => {
      setLoadingOffer(true)
      const response = await OfferModel.getById({ params: { id } })
      if (response.success && response.data?.result) {
        const o = response.data.result
        setStep1({
          name: o.name,
          status: o.status,
          type: o.type,
          starts_at: o.starts_at,
          ends_at: o.ends_at,
          min_order_value: o.min_order_value,
          max_total_uses: o.max_total_uses,
          max_uses_per_customer: o.max_uses_per_customer,
          coupon_allowed: o.coupon_allowed,
        })
        setStep2({ offer_rules: o.offer_rules })
        setStep3({ ui_content: o.ui_content ?? { template: 'grid' } })
        setSavedOffer({ id: o._id, slug: o.slug, updatedAt: o.updated_at })
        // Offer a local draft only when it is newer than what the server has.
        const draft = readDraft(draftKey(id))
        if (draft && (!o.updated_at || draft.savedAt > Date.parse(o.updated_at))) setRestorable(draft)
      } else {
        toast.error('Campaign not found')
        navigate('/campaigns')
      }
      loaded.current = true
      setLoadingOffer(false)
    })()
  }, [id])

  // ── Autosave to this browser (FR-CB4) ─────────────────────────────
  const [dirty, setDirty] = useState(false)
  const firstRender = useRef(true)
  useEffect(() => {
    if (!loaded.current) return
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    setDirty(true)
    const timer = setTimeout(() => writeDraft(draftKey(savedOffer?.id ?? id), { step1, step2, step3, savedAt: Date.now() }), 1500)
    return () => clearTimeout(timer)
  }, [step1, step2, step3, savedOffer?.id, id])

  const restoreDraft = () => {
    if (!restorable) return
    setStep1(restorable.step1)
    setStep2(restorable.step2)
    setStep3(restorable.step3)
    setRestorable(null)
  }
  const discardDraft = () => {
    writeDraft(draftKey(savedOffer?.id ?? id), null)
    setRestorable(null)
  }

  // ── Type change — atomically resets offer_rules and prunes any page
  // blocks that don't exist in the new type's palette ─────────────────
  const handleTypeChange = (type: OfferModelType.OfferType) => {
    setStep1(prev => ({ ...prev, type }))
    setStep2({ offer_rules: getDefaultRules(type) })
    setStep3(prev => {
      const validTypes = new Set(BLOCK_CATALOG[type].map(b => b.type))
      const blocks = prev.ui_content.blocks?.filter(b => validTypes.has(b.type))
      return { ui_content: { ...prev.ui_content, blocks } }
    })
  }

  const setPromotion = (patch: CampaignPromotion) =>
    setStep3(prev => ({ ui_content: { ...prev.ui_content, promote: { ...prev.ui_content.promote, ...patch } } }))

  // ── Submit ────────────────────────────────────────────────────────
  const [submitLoading, setSubmitLoading] = useState(false)

  // The bundle and get-item editors track product_id per row purely to drive the
  // variation picker UI — the backend only accepts product_variation_id there,
  // so it must be stripped before submit.
  const buildOfferRules = (): OfferModelType.OfferRules => {
    if (step1.type === 'buy_x_get_y') {
      const rules = step2.offer_rules as Partial<OfferModelType.BuyXGetYRules>
      return {
        ...rules,
        get_items: (rules.get_items ?? []).map(({ product_id: _productId, ...item }) => item),
      } as OfferModelType.OfferRules
    }
    if (step1.type === 'combo_fixed_price') {
      const rules = step2.offer_rules as Partial<OfferModelType.ComboFixedPriceRules>
      return {
        ...rules,
        items: (rules.items ?? []).map(({ product_variation_id, quantity }) => ({
          product_variation_id,
          quantity,
        })),
      } as OfferModelType.OfferRules
    }
    return step2.offer_rules as OfferModelType.OfferRules
  }

  const buildBody = (overrides: Partial<Pick<WizardStep1, 'status' | 'starts_at'>> = {}) => ({
    name: step1.name,
    status: overrides.status ?? step1.status,
    type: step1.type!,
    offer_rules: buildOfferRules(),
    starts_at: overrides.starts_at ?? step1.starts_at,
    ends_at: step1.ends_at,
    min_order_value: step1.min_order_value,
    max_total_uses: step1.max_total_uses,
    max_uses_per_customer: step1.max_uses_per_customer,
    coupon_allowed: step1.coupon_allowed,
    is_campaign: true,
    ui_content: step3.ui_content,
  })

  // The offer step must be complete before the campaign can be saved at all.
  const validateOffer = (): boolean => {
    const errors = [...validateStep(1, step1, step2), ...validateStep(2, step1, step2)]
    setFormErrors(errors)
    return errors.length === 0
  }

  // Shared create/update — returns the persisted offer on success (and
  // records its id/slug), or null on failure (toasting the error either way).
  const persist = async (overrides: Partial<Pick<WizardStep1, 'status' | 'starts_at'>> = {}): Promise<OfferModelType.Offer | null> => {
    const body = buildBody(overrides)
    const targetId = savedOffer?.id ?? id
    const response = targetId
      ? await OfferModel.update({ params: { id: targetId }, body })
      : await OfferModel.create({ body })
    if (response.success && response.data?.result) {
      const o = response.data.result
      setSavedOffer({ id: o._id, slug: o.slug, updatedAt: o.updated_at })
      setStep1(prev => ({ ...prev, status: o.status, starts_at: o.starts_at }))
      writeDraft(draftKey(targetId), null)
      writeDraft(draftKey(o._id), null)
      setDirty(false)
      // A new campaign now has a URL; the router is not told, so the builder keeps its state.
      if (!targetId) window.history.replaceState(window.history.state, '', `/campaigns/${o._id}`)
      return o
    }
    toast.error(response.error?.data?.message || 'Failed to save campaign')
    return null
  }

  /** Saves without changing the status (a new campaign is saved switched off). */
  const saveDraft = async (): Promise<OfferModelType.Offer | null> => {
    if (!validateOffer()) {
      setStep('offer')
      return null
    }
    setSubmitLoading(true)
    const saved = await persist()
    setSubmitLoading(false)
    if (saved) {
      toast.success('Campaign saved')
      if (saved.status === 'active' && origin) revalidateStore(origin).catch(() => undefined)
    }
    return saved
  }

  /** Moves between steps; leaving the offer step saves the campaign once so it can be previewed. */
  const goTo = async (next: BuilderStep) => {
    if (next === step) return
    if (next !== 'offer' && !savedOffer) {
      if (!validateOffer()) {
        setStep('offer')
        return
      }
      setSubmitLoading(true)
      const saved = await persist()
      setSubmitLoading(false)
      if (!saved) return
    } else if (next !== 'offer' && !validateOffer()) {
      setStep('offer')
      return
    }
    setStep(next)
  }

  // ── Launch ────────────────────────────────────────────────────────
  const catalog = useOfferCatalog(step2.offer_rules)
  const checklist = useMemo(
    () =>
      campaignChecklist({
        name: step1.name,
        type: step1.type,
        rules: step2.offer_rules,
        starts_at: step1.starts_at,
        ends_at: step1.ends_at,
        max_total_uses: step1.max_total_uses,
        blocks: step3.ui_content.blocks ?? [],
        stock: catalog.loading ? undefined : catalog.stock,
        hasImages: catalog.loading ? undefined : catalog.hasImages,
        mobileReviewed,
      }),
    [step1, step2, step3, catalog, mobileReviewed],
  )

  /** "now" starts a scheduled campaign immediately; "schedule" keeps the start date. */
  const publish = async (when: 'now' | 'schedule') => {
    if (!validateOffer()) {
      setStep('offer')
      return
    }
    if (!canPublish(checklist)) {
      toast.error('Fix the items marked in the checklist before publishing')
      return
    }
    const startsLater = Date.parse(step1.starts_at) > Date.now()
    setSubmitLoading(true)
    const saved = await persist({
      status: 'active',
      ...(when === 'now' && startsLater ? { starts_at: new Date().toISOString() } : {}),
    })
    setSubmitLoading(false)
    if (!saved) return
    if (origin) revalidateStore(origin).catch(() => toast.warning('Published. The store may take up to a minute to show it.'))
    const status = campaignStatus(saved)
    toast.success(status === 'scheduled' ? 'Campaign scheduled' : 'Campaign is live')
  }

  /** Switches a live or scheduled campaign off without losing anything. */
  const pause = async () => {
    setSubmitLoading(true)
    const saved = await persist({ status: 'inactive' })
    setSubmitLoading(false)
    if (saved) {
      if (origin) revalidateStore(origin).catch(() => undefined)
      toast.success('Campaign paused')
    }
  }

  // ── Preview in a new tab (saved version) ──────────────────────────
  const previewCampaign = async () => {
    const saved = await saveDraft()
    if (saved) await previewOffer({ _id: saved._id, slug: saved.slug })
  }

  const status = campaignStatus({ status: step1.status, starts_at: step1.starts_at, ends_at: step1.ends_at })

  return {
    isEditMode,
    loadingOffer,
    formErrors,
    step,
    goTo,
    step1,
    setStep1,
    handleTypeChange,
    step2,
    setStep2,
    step3,
    setStep3,
    setPromotion,
    status,
    dirty,
    restorable,
    restoreDraft,
    discardDraft,
    savedOffer,
    domain,
    origin,
    catalog,
    checklist,
    mobileReviewed,
    setMobileReviewed,
    submitLoading,
    saveDraft,
    publish,
    pause,
    previewLoading,
    previewCampaign,
  }
}

export type CampaignBuilderState = ReturnType<typeof useCampaignBuilder>
